import { useState } from 'react'
import { clsx } from 'clsx'
import { useResponsive } from '@/contexts/ResponsiveContext'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from './LoadingSpinner'

/**
 * Responsive Table component that adapts to different screen sizes
 */
const ResponsiveTable = ({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No data available',
  onRowClick,
  className = '',
  tableClassName = '',
  mobileCardClassName = '',
  showMobileCards = true,
  sortable = false,
  onSort,
  sortConfig = null,
  ...props
}) => {
  const { isMobile, isTablet, shouldShowFullTable } = useResponsive()
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Filter columns based on screen size
  const getVisibleColumns = () => {
    if (shouldShowFullTable) {
      return columns
    } else if (isTablet) {
      // Show priority columns on tablet
      return columns.filter(col => col.priority !== 'low').slice(0, 4)
    } else {
      // Show only essential columns on mobile
      return columns.filter(col => col.priority === 'high' || col.essential).slice(0, 2)
    }
  }

  const visibleColumns = getVisibleColumns()
  const hiddenColumns = columns.filter(col => !visibleColumns.includes(col))

  // Handle row expansion on mobile
  const toggleRowExpansion = (rowIndex) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex)
    } else {
      newExpanded.add(rowIndex)
    }
    setExpandedRows(newExpanded)
  }

  // Handle sorting
  const handleSort = (column) => {
    if (!sortable || !onSort || !column.sortable) return

    const direction = 
      sortConfig?.key === column.key && sortConfig?.direction === 'asc' 
        ? 'desc' 
        : 'asc'
    
    onSort({ key: column.key, direction })
  }

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    )
  }

  // Render empty state
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    )
  }

  // Mobile card layout
  if (isMobile && showMobileCards) {
    return (
      <div className={clsx('space-y-4', className)} {...props}>
        {data.map((row, rowIndex) => (
          <div
            key={row.id || rowIndex}
            className={clsx(
              'bg-white rounded-lg shadow-sm border border-gray-200 p-4',
              onRowClick && 'cursor-pointer hover:shadow-md transition-shadow duration-200',
              mobileCardClassName
            )}
            onClick={() => onRowClick?.(row, rowIndex)}
          >
            {/* Essential information */}
            <div className="space-y-3">
              {visibleColumns.map((column) => (
                <div key={column.key} className="flex justify-between items-start">
                  <span className="text-sm font-medium text-gray-500 min-w-0 flex-1">
                    {column.header}
                  </span>
                  <span className="text-sm text-gray-900 ml-2 text-right">
                    {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                  </span>
                </div>
              ))}
            </div>

            {/* Expandable section for additional data */}
            {hiddenColumns.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleRowExpansion(rowIndex)
                  }}
                  className="mt-3 flex items-center text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200"
                >
                  {expandedRows.has(rowIndex) ? (
                    <>
                      <ChevronUpIcon className="h-4 w-4 mr-1" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDownIcon className="h-4 w-4 mr-1" />
                      Show more
                    </>
                  )}
                </button>

                {expandedRows.has(rowIndex) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {hiddenColumns.map((column) => (
                      <div key={column.key} className="flex justify-between items-start">
                        <span className="text-sm font-medium text-gray-500 min-w-0 flex-1">
                          {column.header}
                        </span>
                        <span className="text-sm text-gray-900 ml-2 text-right">
                          {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Traditional table layout for tablet and desktop
  return (
    <div className={clsx('overflow-hidden', className)} {...props}>
      <div className="overflow-x-auto">
        <table className={clsx('min-w-full divide-y divide-gray-200', tableClassName)}>
          {/* Table header */}
          <thead className="bg-gray-50">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
                    sortable && column.sortable && 'cursor-pointer hover:bg-gray-100 select-none',
                    column.headerClassName
                  )}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {sortable && column.sortable && sortConfig?.key === column.key && (
                      <span className="text-gray-400">
                        {sortConfig.direction === 'asc' ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className={clsx(
                  'hover:bg-gray-50 transition-colors duration-150',
                  onRowClick && 'cursor-pointer',
                  rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                )}
                onClick={() => onRowClick?.(row, rowIndex)}
              >
                {visibleColumns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
                      column.cellClassName
                    )}
                  >
                    {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Table column helper for creating column definitions
 */
export const createColumn = ({
  key,
  header,
  render,
  sortable = false,
  priority = 'medium', // 'high', 'medium', 'low'
  essential = false,
  headerClassName = '',
  cellClassName = ''
}) => ({
  key,
  header,
  render,
  sortable,
  priority,
  essential,
  headerClassName,
  cellClassName
})

/**
 * Pagination component for tables
 */
export const TablePagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showPageNumbers = true,
  className = ''
}) => {
  const { isMobile } = useResponsive()

  const getVisiblePages = () => {
    const delta = isMobile ? 1 : 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  if (totalPages <= 1) return null

  return (
    <div className={clsx(
      'flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6',
      className
    )}>
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        
        {showPageNumbers && (
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              {getVisiblePages().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' && onPageChange(page)}
                  disabled={typeof page !== 'number'}
                  className={clsx(
                    'relative inline-flex items-center px-4 py-2 text-sm font-semibold',
                    typeof page !== 'number' 
                      ? 'text-gray-700 cursor-default'
                      : page === currentPage
                        ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0',
                    index === 0 && 'rounded-l-md',
                    index === getVisiblePages().length - 1 && 'rounded-r-md'
                  )}
                >
                  {page}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </div>
  )
}

export default ResponsiveTable